import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, CircularProgress, Paper, Tab, Tabs, Toolbar, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { createAccount } from "../../../api/account";
import { getResources } from "../../../api/app";
import AddAccountForm, { ADD_ACCOUNT_FORM_ID } from "../../../components/Accounts/AddAccountForm";
import PermissionsForm from "../../../components/Accounts/PermissionsForm";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { setLoading, setTab } from "../../../store/UI";
import { CreateAccountForm } from "../../../types/forms/admin/account";
import { Role } from "../../../types/interfaces/account";
import { PrivateSettings } from "../../../types/interfaces/app";
import { CreateAccountRequest } from "../../../types/model/account";
import { Resource } from "../../../types/model/app/PrivateSettings";
import { StoreInterfaces, UIStore } from "../../../types/store";

const AddAccount = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const handleError = useErrorHandler();

  const privateSettings = useSelector(
    (state: StoreInterfaces) => state.app.privateSettings,
    shallowEqual
  ) as PrivateSettings | undefined;
  const tabState = useSelector((state: { ui: UIStore }) => state.ui.tabState);

  const [isSaving, setIsSaving] = useState(false);
  const [tabIndex, setTabIndex] = useState<number>(
    (tabState[location.pathname] as number) || 0
  );
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());

  const childrenOf = (parentKey: string) =>
    resources.filter((r) => r.parentKey === parentKey && r.active);

  const handleTabChange = (_: any, newValue: number) => {
    setTabIndex(newValue);
    dispatch(setTab({ page: location.pathname, tab: newValue }));
  };

  const handleTogglePermission = (key: string, isParent: boolean) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      const children = childrenOf(key).map((c) => c.key);

      if (next.has(key)) {
        next.delete(key);
        if (isParent) children.forEach((c) => next.delete(c));
      } else {
        next.add(key);
        if (isParent) children.forEach((c) => next.add(c));
      }

      if (!isParent) {
        const parent = resources.find((r) =>
          childrenOf(r.key).some((c) => c.key === key)
        );
        if (parent) {
          const allSiblings = childrenOf(parent.key).map((c) => c.key);
          if (allSiblings.every((s) => !next.has(s))) next.delete(parent.key);
          else next.add(parent.key);
        }
      }

      return next;
    });
  };

  const handleCreateClick = () => {
    const form = document.getElementById(ADD_ACCOUNT_FORM_ID) as HTMLFormElement | null;
    form?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
  };

  const fetchResources = async () => {
    setResourcesLoading(true);
    try {
      const data = await getResources();
      setResources(data);
      const allActiveKeys = new Set(data.filter((r) => r.active).map((r) => r.key));
      setCheckedKeys(allActiveKeys);
      setResourcesLoaded(true);
    } catch (error) {
      handleError(error);
    } finally {
      setResourcesLoading(false);
    }
  };

  const handleSaveAction = async (values: CreateAccountForm) => {
    try {
      setIsSaving(true);
      dispatch(setLoading(true));

      const resourcePermissions = resources
        .filter((r) => r.active && checkedKeys.has(r.key))
        .map((r) => ({ resourceKey: r.key, active: true }));

      const payload: CreateAccountRequest = { ...values, resourcePermissions };

      const response = await createAccount(payload);
      navigate(`/admin/accounts/${response.id}`, { replace: true });
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    if (privateSettings?.roles) setAvailableRoles(privateSettings.roles);
  }, [privateSettings]);

  useEffect(() => {
    if (!resourcesLoaded) fetchResources();
  }, [resourcesLoaded]);

  return (
    <Paper elevation={3} sx={{ borderRadius: 2 }}>
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Typography variant="h4">
          {t("admin.accounts.table.add-account")}
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            sx={{ borderRadius: 2 }}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleCreateClick}
            disabled={isSaving}
          >
            {t("generic.create")}
          </Button>

          <Tooltip title={t("generic.back")}>
            <Button
              onClick={() => navigate(-1)}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              <ArrowBackIcon />
            </Button>
          </Tooltip>
        </Box>
      </Toolbar>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        <Tab label={t("admin.accounts.tabs.information")} />
        <Tab label={t("admin.accounts.tabs.permissions")} />
      </Tabs>

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: tabIndex === 0 ? "block" : "none" }}>
          <AddAccountForm
            onSubmit={handleSaveAction}
            availableRoles={availableRoles}
          />
        </Box>

        <Box sx={{ display: tabIndex === 1 ? "block" : "none" }}>
          {resourcesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <PermissionsForm
              resources={resources}
              loading={resourcesLoading}
              checkedKeys={checkedKeys}
              onToggle={handleTogglePermission}
              isEditing={true}
              isSaving={false}
              onEdit={() => {}}
              onSave={() => {}}
              onCancel={() => {}}
              showHeader={false}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default AddAccount;